import type { Collection, CollectionDefaults, FindOptions, NormalizedRelation, ResolvedCollection, StoreCore, StoreSchema } from '@rstore/shared';

type VisitHook<TSchema extends StoreSchema, TCollectionDefaults extends CollectionDefaults> = <
	NestedCollection extends ResolvedCollection<Collection, TCollectionDefaults, TSchema>,
>(
	collection: NestedCollection,
	relationKey: string,
	relation: NormalizedRelation,
) => void;

interface VisitCollectionsFromFindOptionsOptions<
	TStore extends StoreCore<TSchema, TCollectionDefaults>,
	TCollection extends Collection,
	TSchema extends StoreSchema,
	TCollectionDefaults extends CollectionDefaults,
> {
	store: TStore;
	collection: ResolvedCollection<TCollection, TCollectionDefaults, TSchema>;
	findOptions?: FindOptions<TCollection, TCollectionDefaults, TSchema>;
	visit?: VisitHook<TSchema, TCollectionDefaults>;
	preVisitNested?: VisitHook<TSchema, TCollectionDefaults>;
	postVisitNested?: VisitHook<TSchema, TCollectionDefaults>;
}

/**
 * Recursively traverses relation includes from findOptions and executes visitor callbacks for each collection
 */
export function visitCollectionsFromFindOptions<
	TStore extends StoreCore<TSchema, TCollectionDefaults>,
	TCollection extends Collection,
	TSchema extends StoreSchema,
	TCollectionDefaults extends CollectionDefaults,
>(options: VisitCollectionsFromFindOptionsOptions<TStore, TCollection, TSchema, TCollectionDefaults>) {
	const { store, collection, findOptions, visit, preVisitNested, postVisitNested } = options;

	if (findOptions?.include && typeof findOptions?.include === 'object') {
		for (const relationKey of Object.keys(findOptions.include)) {
			const includeOptions = findOptions.include[relationKey as keyof TCollection['relations']];

			if (!includeOptions) continue;

			const relation = collection.normalizedRelations[relationKey];

			if (!relation) {
				console.error(`Failed to find relation ${relationKey} on collection ${collection.name}`);
				continue;
			}

			const collectionNames = new Set(relation.to.map((t) => t.collection));

			if (collectionNames.size > 1) {
				// TODO polymorphic relations
				throw new Error("Polymorphic relations aren't yet supported in relational fetch");
			}
			else if (collectionNames.size === 0) {
				continue;
			}

			const [collectionName] = collectionNames;

			const toCollection = store.$collections.find((m) => m.name === collectionName);

			if (!toCollection) {
				throw new Error(`Could not find collection ${collectionName} for relation ${relationKey} on collection ${collection.name}`);
			}

			visit?.(toCollection, relationKey, relation);

			preVisitNested?.(toCollection, relationKey, relation);

			visitCollectionsFromFindOptions({
				...options,
				collection: toCollection,
				findOptions: { include: includeOptions },
			});

			postVisitNested?.(toCollection, relationKey, relation);
		}
	}
}
